import {addDays} from 'date-fns';
import {
  Alert,
  Button,
  CurrencyInput,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
  openConfirmDialog,
} from 'platform/components';
import {Box, Grid, GridItem, Hide, Right, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {boolean, mixed, object} from 'yup';

import {startTransition} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {always, head, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isNumber} from 'ramda-adjunct';

import {useGetParticipationQuery, useGetTenantQuery} from '@dms/api/core';
import {OrderResponseBody, useLazyFindConversionRateQuery} from '@dms/api/finance';
import {
  OrderDiscriminator,
  OrderPaymentResponseBody,
  useEditPurchasePaymentMutation,
  useEditSaleBalancePaymentMutation,
  useEditSaleDepositPaymentMutation,
  useForbidDepositMutation,
  useGetBusinessCaseQuery,
  useGetListDueDateSettingQuery,
  useGetOrderQuery,
  useIssuePurchasePaymentMutation,
  useIssueSaleBalancePaymentMutation,
  useIssueSaleDepositPaymentMutation,
  useRemovePurchasePaymentMutation,
  useRemoveSaleDepositPaymentMutation,
} from '@dms/api/sales';
import {ApiError, EntityResourceIds} from '@dms/api/shared';
import {ConditionEnum, useGetSaleVehicleQuery, useGetVehicleQuery} from '@dms/api/vehicle';
import {useGetVehicleWarehouseManagementSettingsQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DEFAULT_CURRENCY,
  handleApiError,
  NoPermissionTooltip,
  PredefinedNotes,
  useBank,
  useGetCurrentBranch,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
  usePermissions,
  useVehicleWarehouse,
} from '@dms/shared';

import {
  getApiDateString,
  isApiDateString,
  isNilOrZero,
  noop,
  Nullish,
  parseDate,
  suffixTestId,
  TestIdProps,
  useDebouncedCallback,
  useRequiredParams,
  yupNumber,
  yupString,
} from 'shared/web';

import {getPaymentsByDiscriminator} from '../../utils/getPaymentsByDiscriminator';
import {CustomerBankAccountField} from './components/CustomerBankAccountField';
import {ForeignCurrencyFields} from './components/ForeignCurrencyFields';
import {PaymentAmountSync} from './components/PaymentAmountSync';
import {TenantBankAccountField} from './components/tenantBankAccountField';
import {useGetSubmitBody} from './hooks/useGetSubmitBody';
import {FormValuesType} from './types/FormValuesType';
import {getDepositPaymentFormHandlers} from './utils/getDepositPaymentFormHandlers';
import {getPaymentMethodOptions} from './utils/getPaymentMethodOptions';
import {getPercentage, HUNDRED_PERCENT} from './utils/getPercentage';
import {getPredefinedNote} from './utils/getPredefinedNote';

interface PaymentFormProps extends TestIdProps {
  payment: OrderPaymentResponseBody;
  checkoutId: string;
  order: OrderResponseBody;
  refreshBusinessCaseCheckoutInfo: (customerId: string | Nullish) => void;
  isBrokerageSale?: boolean;
  handleAddAnotherPayment: () => void;
  canAddAnotherPayment: boolean;
  isAddPaymentPending: boolean;
  isFetchingOrder: boolean;
}

const DUE_DATE_DEFAULT_OFFSET = '14';
const DEFAULT_PRICE = '0';

export function IssuePaymentForm(props: PaymentFormProps) {
  const {bankAccountOptions, isLoading: isLoadingBank, primaryBankAccountOption} = useBank();
  const {isMandatoryFieldsEnabled} = useMandatoryFields();
  const {activeBranchId} = useGetCurrentBranch();
  const {id: businessCaseId} = useRequiredParams();
  const {data: tenant} = useGetTenantQuery();
  const [findConversionRate, {isLoading: isLoadingConversionTables}] =
    useLazyFindConversionRateQuery();

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId});

  const [
    canIssuePurchasePayment,
    canIssueSellingPayment,
    canIssueDepositPayment,
    canIssueFromVehicleWarehouse,
    canCancelSellingInvoice,
  ] = usePermissions({
    permissionKeys: [
      'issueBusinessCasePurchasePayment',
      'issueBusinessCaseSellingPayment',
      'issueBusinessCaseSellingDeposit',
      'vehicleIssueFromVehicleWarehouse',
      'cancelBusinessCaseSellingInvoice',
    ],
    scopes: {
      issueBusinessCasePurchasePayment: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      issueBusinessCaseSellingPayment: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      issueBusinessCaseSellingDeposit: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      cancelBusinessCaseSellingInvoice: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const isIssuePaymentButtonDisabled =
    isLoadingConversionTables ||
    props.order.orderDiscriminator === 'PURCHASE' ||
    props.order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE' ||
    props.order.orderDiscriminator === 'PURCHASE_BROKERAGE_FEES'
      ? !canIssuePurchasePayment
      : !canIssueSellingPayment;

  const isCancelInvoiceButtonDisabled =
    isLoadingConversionTables ||
    props.order.orderDiscriminator === 'PURCHASE' ||
    props.order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE' ||
    props.order.orderDiscriminator === 'PURCHASE_BROKERAGE_FEES'
      ? false
      : !canCancelSellingInvoice;

  const {data: dueDateSettings, isLoading: isLoadingDueDateSettings} =
    useGetListDueDateSettingQuery();
  const activeSettings =
    dueDateSettings?.find((item) => item.branchId === activeBranchId) ??
    dueDateSettings?.find((item) => item.isSystem);

  const dueDate =
    props.payment.paymentDiscriminator === 'DEPOSIT'
      ? activeSettings?.proformaInvoiceDueDate
      : activeSettings?.invoiceDueDate;

  const dueDateOffset = Number(dueDate ?? DUE_DATE_DEFAULT_OFFSET);

  const vehicleId = businessCaseData?.offers?.[0]?.saleVehicles?.[0]?.vehicleId;

  const {data: vehicleData} = useGetSaleVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNilOrEmpty(vehicleId)}
  );

  const {data: originalVehicleData} = useGetVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNilOrEmpty(vehicleId)}
  );

  const {openStockInAlertDialog} = useVehicleWarehouse({
    vehicleId: vehicleId ?? '',
  });

  const {data: branchData, isLoading: isLoadingBranchData} = useGetCurrentBranch();

  const {isLoading: isLoadingOrder} = useGetOrderQuery({
    checkoutId: props.checkoutId,
    orderId: props.order.id,
  });

  const [removeSaleDepositPayment, {isLoading: isRemoveSaleDepositPaymentMutationLoading}] =
    useRemoveSaleDepositPaymentMutation();
  const [removePurchasePayment, {isLoading: isRemovePurchasePaymentMutationLoading}] =
    useRemovePurchasePaymentMutation();
  const [editPurchasePayment] = useEditPurchasePaymentMutation();
  const [editSaleDepositPayment] = useEditSaleDepositPaymentMutation();
  const [editSaleBalancePayment] = useEditSaleBalancePaymentMutation();
  const [issuePurchasePayment] = useIssuePurchasePaymentMutation();
  const [issueSaleDepositPayment] = useIssueSaleDepositPaymentMutation();
  const [issueSaleBalancePayment] = useIssueSaleBalancePaymentMutation();
  const [getPaymentBody] = useGetSubmitBody();

  const mandatoryFieldsVehicleId =
    businessCaseData?.offers?.[0]?.saleVehicles?.[0]?.vehicleId ??
    businessCaseData?.offers?.[0]?.purchaseVehicles?.[0]?.vehicleId ??
    businessCaseData?.offers?.[0]?.purchaseBrokerageVehicles?.[0]?.vehicleId;

  const {handleMandatoryFieldsError, isMandatoryFieldsError} =
    useMandatoryFieldsErrorDialog(mandatoryFieldsVehicleId);

  const isAnyDepositUnpaid = props.order.payments.some(
    (payment) => payment.paymentDiscriminator === 'DEPOSIT' && payment.paymentState !== 'PAID'
  );

  const {balanceAndPurchases, deposits} = getPaymentsByDiscriminator(props.order);
  const isPartialPayment =
    props.payment.paymentDiscriminator === 'DEPOSIT' || balanceAndPurchases.length > 1;

  const customerBankAccounts =
    props.order.contractInformation.customerContractInformation.bankAccounts;

  const depositCurrency = isCurrency(props.order.totalPrice.withVat.currency)
    ? props.order.totalPrice.withVat.currency
    : DEFAULT_CURRENCY;

  const paymentPrice = props.payment.amount?.amount ?? DEFAULT_PRICE;
  const orderPrice = props.order.totalPrice.withVat.amount ?? DEFAULT_PRICE;

  const handleSyncAmountAndPercentage = (
    val: string | number | null,
    formApi: UseFormReturn<FormValuesType>,
    initiator: 'amount' | 'percentage'
  ) => {
    const orderPriceNumber = Number(orderPrice);
    const maxPrice = initiator === 'amount' ? orderPriceNumber : HUNDRED_PERCENT;
    const updateInput = initiator === 'amount' ? 'percentage' : 'amount';

    if (!isNumber(val) || val === 0) {
      formApi.setValue(updateInput, 0);
      formApi.setValue('exchangeRateAmount', DEFAULT_PRICE);
      return;
    }

    if (val > maxPrice) {
      return formApi.setError(initiator, {
        message: i18n.t('general.errors.number.max', {max: maxPrice}),
      });
    }

    const percentage =
      initiator === 'amount'
        ? (val / orderPriceNumber) * HUNDRED_PERCENT
        : (val / HUNDRED_PERCENT) * orderPriceNumber;

    formApi.setValue(updateInput, Number(percentage.toFixed(2)));

    formApi.clearErrors(['amount', 'percentage']);
    handleUpdateForeignCurrency(formApi);
  };

  const debouncedHandleSyncAmountAndPercentage = useDebouncedCallback(
    handleSyncAmountAndPercentage,
    300
  );

  const handleSyncPaymentDatesWithPaymentMethod =
    (formApi: UseFormReturn<FormValuesType>) => (value: string | number | null) =>
      startTransition(() => {
        if (isNil(value)) {
          return;
        }
        const {issuedDate} = formApi.getValues();

        match(value)
          .with('CASH', 'PAYMENT_CARD', () => {
            formApi.setValue('dueSinceIssueDateInDays', 0);
            formApi.setValue('dueDate', issuedDate);
          })
          .with('BANK_TRANSFER', () => {
            formApi.setValue('dueSinceIssueDateInDays', dueDateOffset);
            formApi.setValue(
              'dueDate',
              getApiDateString(addDays(parseDate(issuedDate), dueDateOffset))
            );
          })
          .otherwise(noop);
      });

  const isDeductible = props.order?.items.some((item) => item.deductible);
  const isDeductibleAndInland = props.order?.typeOfSale === 'inland' && isDeductible;

  const note = match([
    props.order.orderDiscriminator,
    props.payment.paymentDiscriminator,
    props.isBrokerageSale,
    isDeductibleAndInland,
  ])
    .with(
      ['SALE', 'BALANCE', false, false],
      always(props.payment.comment || getPredefinedNote(props.order.typeOfSale) || '')
    )
    .otherwise(always(props.payment.comment ?? ''));

  const foreignCurrencyAccount = branchData?.billingInformation?.bankAccounts.find(
    (item) => item.currency === props.payment.foreignCurrencyAmount?.currency
  )?.accountNumber;

  const defaultValues: Partial<FormValuesType> = {
    amount: parseFloat(paymentPrice ?? DEFAULT_PRICE),
    percentage: getPercentage(props.payment.amount?.amount, orderPrice),
    paymentMethod: 'BANK_TRANSFER',
    tenantBankAccount:
      foreignCurrencyAccount ?? primaryBankAccountOption?.value ?? head(bankAccountOptions)?.value,
    exchangeRate: props.payment?.exchangeRate ?? null,
    customerBankAccountId: head(customerBankAccounts ?? [])?.id,
    issuedDate: getApiDateString(new Date()),
    dateOfTaxableSupply: getApiDateString(new Date()),
    dueDate: getApiDateString(addDays(new Date(), dueDateOffset)),
    dueSinceIssueDateInDays: dueDateOffset,
    supplierInvoiceNumber: props.payment.supplierInvoiceNumber,
    note,
  };
  const {openStockOutDialog} = useVehicleWarehouse({
    vehicleId: vehicleId ?? '',
    vehicleWarehouseId: vehicleData?.vehicleWarehouse?.id ?? '',
  });

  const {data: warehouseManagmentData} = useGetVehicleWarehouseManagementSettingsQuery();

  const hasWarehouseManagementEnabled = warehouseManagmentData?.isEnabled;

  const handleUpdatePayment: FormSubmitHandler<FormValuesType> = async (data) => {
    const editPaymentRequestBody = getPaymentBody(data);
    const shouldOpenStockoutDialog =
      canIssueFromVehicleWarehouse &&
      isNotNilOrEmpty(vehicleData?.vehicleWarehouse?.id) &&
      props.payment.paymentDiscriminator === 'BALANCE' &&
      (data.percentage === 100 || data.percentage === 0);

    const shouldOpenStockInAlertDialog =
      isNilOrEmpty(vehicleData?.vehicleWarehouse?.id) &&
      businessCaseData?.businessCaseType === 'SELLING' &&
      props.payment.paymentDiscriminator === 'BALANCE' &&
      data.percentage === 100 &&
      vehicleData?.type !== 'BROKERAGE' &&
      isNotNilOrEmpty(vehicleData?.buyingState) &&
      isNotNilOrEmpty(vehicleData?.purchasePrice?.withoutVat) &&
      hasWarehouseManagementEnabled;

    const paymentProps = {
      businessCaseId,
      orderId: props.order.id,
      paymentId: props.payment.id,
    };

    const editPaymentMutation =
      // eslint-disable-next-line no-nested-ternary
      props.payment.paymentDiscriminator === 'PURCHASE'
        ? editPurchasePayment
        : props.payment.paymentDiscriminator === 'DEPOSIT'
          ? editSaleDepositPayment
          : editSaleBalancePayment;

    await editPaymentMutation({
      ...paymentProps,
      editPaymentRequestBody,
    })
      .unwrap()
      .then(async () => {
        const issuePaymentMutation =
          // eslint-disable-next-line no-nested-ternary
          props.payment.paymentDiscriminator === 'PURCHASE'
            ? issuePurchasePayment
            : props.payment.paymentDiscriminator === 'DEPOSIT'
              ? issueSaleDepositPayment
              : issueSaleBalancePayment;

        await issuePaymentMutation({
          ...paymentProps,
          validateMandatoryFields: isMandatoryFieldsEnabled(
            (originalVehicleData?.condition as ConditionEnum) ?? undefined
          ),
          issuePaymentRequestBody: {
            issuedOn: data.issuedDate,
          },
        })
          .unwrap()
          .then((data) => {
            if (shouldOpenStockoutDialog) {
              openConfirmDialog({
                text: i18n.t('entity.vehicleWarehouse.modal.stockOutDescription'),
                onConfirm: () => openStockOutDialog(data.invoiceId ?? undefined),
              });
            }
            if (shouldOpenStockInAlertDialog && isNotNil(data.invoiceId)) {
              openStockInAlertDialog(data.invoiceId, branchData?.id);
            }
          })
          .catch((error: ApiError) => {
            if (isMandatoryFieldsError(error)) {
              return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
            }

            return handleApiError(error);
          });
      })
      .catch(handleApiError);
  };

  const handleUpdateForeignCurrency = (formApi: UseFormReturn<FormValuesType>) => {
    const data = formApi.getValues();
    const editPaymentRequestBody = getPaymentBody(data);

    if (isNil(props.payment.exchangeRate)) {
      return;
    }
    if (isNilOrZero(data.amount) || isNilOrZero(parseFloat(data.exchangeRate ?? DEFAULT_PRICE))) {
      formApi.setValue('exchangeRateAmount', DEFAULT_PRICE);
      return;
    }

    const editPaymentMutation =
      // eslint-disable-next-line no-nested-ternary
      props.payment.paymentDiscriminator === 'PURCHASE'
        ? editPurchasePayment
        : props.payment.paymentDiscriminator === 'DEPOSIT'
          ? editSaleDepositPayment
          : editSaleBalancePayment;

    editPaymentMutation({
      businessCaseId,
      orderId: props.order.id,
      paymentId: props.payment.id,
      editPaymentRequestBody,
    })
      .unwrap()
      .then((data) => {
        const payment = data.payments.find((item) => item.id === props.payment.id);

        formApi.setValue('exchangeRate', payment?.exchangeRate || null);
        formApi.setValue('exchangeRateAmount', payment?.foreignCurrencyAmount?.amount || null);
      })
      .catch(handleApiError);
  };

  const [forbitDeposit] = useForbidDepositMutation();

  const handleDeletePayment = () => {
    const isDepositPayment = props.payment.paymentDiscriminator === 'DEPOSIT';

    if (isDepositPayment && deposits.length <= 1) {
      forbitDeposit({checkoutId: props.checkoutId, orderId: props.order.id})
        .unwrap()
        .then(() => props.refreshBusinessCaseCheckoutInfo(null))
        .catch(handleApiError);

      return;
    }

    const deletePaymentMutation = isDepositPayment
      ? removeSaleDepositPayment
      : removePurchasePayment;

    deletePaymentMutation({
      businessCaseId,
      orderId: props.order.id,
      paymentId: props.payment.id,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleOnDotsChange =
    (formApi: UseFormReturn<FormValuesType>) => (dateOfTaxableSupply: string | null) => {
      const domesticCurrency = isNotNil(tenant) ? tenant.currency : undefined;
      const foreignCurrency = props.payment.foreignCurrencyAmount?.currency;

      const isValidDate = isApiDateString(dateOfTaxableSupply);

      if (
        isNil(domesticCurrency) ||
        isNil(dateOfTaxableSupply) ||
        isNil(foreignCurrency) ||
        !isValidDate
      ) {
        return;
      }

      findConversionRate({domesticCurrency, foreignCurrency, validOn: dateOfTaxableSupply})
        .unwrap()
        .then((rate) => {
          const ratio = rate?.ratio ?? '';

          formApi.setValue('exchangeRate', ratio);
        })
        .catch(handleApiError);
    };

  const isOrderPurchase =
    props.order.orderDiscriminator === 'PURCHASE' ||
    props.order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE';
  const isLoading =
    isLoadingBank || isLoadingBranchData || isLoadingOrder || isLoadingDueDateSettings;
  const paymentMethodOptions = getPaymentMethodOptions(
    props.order.orderDiscriminator,
    props.payment.paymentDiscriminator
  );

  const shouldIssuePaymentButtonBeDisabledACL =
    (props.payment.paymentDiscriminator !== 'DEPOSIT' && isIssuePaymentButtonDisabled) ||
    (props.payment.paymentDiscriminator === 'DEPOSIT' && !canIssueDepositPayment);

  return (
    <DataStatus isLoading={isLoading} minHeight={180}>
      <Form<FormValuesType>
        onSubmit={handleUpdatePayment}
        defaultValues={defaultValues}
        schema={getSchema(
          isPartialPayment,
          props.order.orderDiscriminator,
          isNotNil(props.payment.exchangeRate)
        )}
      >
        {(control, formApi) => {
          const [issueDate, paymentMethod, note] = formApi.watch([
            'issuedDate',
            'paymentMethod',
            'note',
          ]);

          const updateNote = (val: string) => formApi.setValue('note', val);
          const minDueDate = issueDate ? parseDate(issueDate) : undefined;

          const isCustomerBankAccountIdError = isNotNil(
            formApi.getFieldState('customerBankAccountId')?.error
          );

          const {handleOnDueChange, handleOnDueDateChange, handleOnIssueDateChange} =
            getDepositPaymentFormHandlers(formApi);

          return (
            <VStack spacing={4}>
              <PaymentAmountSync
                formApi={formApi}
                orderId={props.order.id}
                checkoutId={props.checkoutId}
                paymentId={props.payment.id}
                isPartialPayment={isPartialPayment}
              />
              <Grid columns={4}>
                <Hide when={isPartialPayment}>
                  <CurrencyInput
                    value={parseFloat(props.payment.amount?.amount ?? DEFAULT_PRICE)}
                    data-testid={suffixTestId('amount-currency', props)}
                    currency={depositCurrency}
                    isDisabled
                    label={i18n.t('general.labels.amount')}
                  />
                </Hide>
                <Show when={isPartialPayment}>
                  <FormField
                    control={control}
                    data-testid={suffixTestId('amount', props)}
                    name="amount"
                    type="currency"
                    currency={depositCurrency}
                    onChange={(val) =>
                      debouncedHandleSyncAmountAndPercentage(val, formApi, 'amount')
                    }
                    label={i18n.t('general.labels.amount')}
                  />
                </Show>
                <Show when={isPartialPayment}>
                  <FormField
                    control={control}
                    name="percentage"
                    data-testid={suffixTestId('percentage', props)}
                    type="number"
                    maxStepperValue={HUNDRED_PERCENT}
                    minStepperValue={0}
                    isDisabled={isCustomerBankAccountIdError}
                    onChange={(val) =>
                      debouncedHandleSyncAmountAndPercentage(val, formApi, 'percentage')
                    }
                    suffix="%"
                    label={i18n.t('entity.order.labels.percentage')}
                  />
                </Show>

                <ForeignCurrencyFields
                  checkoutId={props.checkoutId}
                  control={control}
                  formApi={formApi}
                  orderId={props.order.id}
                  payment={props.payment}
                  businessCaseId={businessCaseId}
                  data-testid={props['data-testid']}
                />
              </Grid>
              <Grid columns={4}>
                <FormField
                  control={control}
                  name="issuedDate"
                  onChange={handleOnIssueDateChange}
                  type="apiDate"
                  label={i18n.t('entity.accounting.labels.issueDate')}
                  data-testid={suffixTestId('issueDate', props)}
                  isRequired
                />
                <FormField
                  control={control}
                  name="dueSinceIssueDateInDays"
                  type="number"
                  label={i18n.t('entity.accounting.labels.due')}
                  onChange={handleOnDueChange}
                  data-testid={suffixTestId('dueSinceIssueDateInDays', props)}
                  isStepperVisible
                  step={1}
                  minStepperValue={0}
                />
                <FormField
                  control={control}
                  name="dueDate"
                  data-testid={suffixTestId('dueDate', props)}
                  type="apiDate"
                  label={i18n.t('entity.accounting.labels.dueDate')}
                  minDate={minDueDate}
                  onChange={handleOnDueDateChange}
                  isRequired
                />

                <Hide when={isOrderPurchase}>
                  <FormField
                    control={control}
                    name="dateOfTaxableSupply"
                    data-testid={suffixTestId('dateOfTaxableSupply', props)}
                    onChange={handleOnDotsChange(formApi)}
                    isDisabled={isLoadingConversionTables}
                    type="apiDate"
                    label={i18n.t('entity.accounting.labels.dateOfTaxableSupply')}
                    isRequired
                  />
                </Hide>
                <Show when={isOrderPurchase}>
                  <FormField
                    control={control}
                    name="supplierInvoiceNumber"
                    type="text"
                    label={i18n.t('entity.checkout.labels.supplierInvoiceNumber')}
                    data-testid={suffixTestId('supplierInvoiceNumber', props)}
                  />
                </Show>
              </Grid>

              <Show when={props.order.foreignCurrencyPayment}>
                <GridItem span={4}>
                  <Alert
                    variant="warning"
                    title={i18n.t('entity.checkout.labels.foreignPaymentWarning')}
                    data-testid={suffixTestId('foreignPaymentWarning', props)}
                  />
                </GridItem>
              </Show>

              <Grid columns={4}>
                <GridItem span={2}>
                  <FormField
                    control={control}
                    name="paymentMethod"
                    data-testid={suffixTestId('paymentMethod', props)}
                    type="choice"
                    isNotClearable
                    onChange={handleSyncPaymentDatesWithPaymentMethod(formApi)}
                    options={paymentMethodOptions}
                    label={i18n.t('entity.checkout.labels.paymentMethod')}
                  />
                </GridItem>

                <Show when={paymentMethod === 'BANK_TRANSFER' || paymentMethod === 'OFFSET'}>
                  <GridItem span={2}>
                    <Show
                      when={
                        props.order.orderDiscriminator === 'PURCHASE' ||
                        props.order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE'
                      }
                    >
                      <CustomerBankAccountField
                        checkoutId={props.checkoutId}
                        control={control}
                        formApi={formApi}
                        order={props.order}
                        data-testid={props['data-testid']}
                      />
                    </Show>

                    <TenantBankAccountField
                      control={control}
                      formApi={formApi}
                      data-testid={props['data-testid']}
                      order={props.order}
                    />
                  </GridItem>
                </Show>

                <Show
                  when={
                    props.order.orderDiscriminator === 'SALE' ||
                    props.order.orderDiscriminator === 'PURCHASE_BROKERAGE_FEES'
                  }
                >
                  <GridItem span={4}>
                    <Box position="relative">
                      <div style={{position: 'absolute', right: '0', top: '0'}}>
                        <PredefinedNotes
                          isLinkVariant
                          note={note}
                          onPrefill={updateNote}
                          resource="BUSINESS_CASE"
                          context="invoice"
                          data-testid={suffixTestId('predefinedNotes', props)}
                        />
                      </div>

                      <FormField
                        name="note"
                        type="textarea"
                        minRows={1}
                        control={control}
                        label={i18n.t('entity.checkout.labels.noteForPrint')}
                        data-testid={suffixTestId('note', props)}
                      />
                    </Box>
                  </GridItem>
                </Show>
              </Grid>

              <Right>
                <Show when={props.canAddAnotherPayment}>
                  <Box flexGrow={1}>
                    <Button
                      variant="link"
                      leftIcon="content/add_circle"
                      onClick={props.handleAddAnotherPayment}
                      isLoading={props.isAddPaymentPending}
                      isDisabled={props.isFetchingOrder}
                      title={i18n.t('entity.checkout.addAnotherPayment')}
                      data-testid={testIds.businessCase.checkout('addAnotherPayment')}
                    />
                  </Box>
                </Show>

                <Show when={isPartialPayment}>
                  <NoPermissionTooltip shouldShowTooltip={isCancelInvoiceButtonDisabled}>
                    <FormButton
                      title={i18n.t('entity.checkout.removePayment')}
                      variant="dangerGhost"
                      control={control}
                      type="button"
                      onClick={handleDeletePayment}
                      data-testid={suffixTestId('removePayment', props)}
                      isLoading={
                        isRemoveSaleDepositPaymentMutationLoading ||
                        isRemovePurchasePaymentMutationLoading
                      }
                      isDisabled={isCancelInvoiceButtonDisabled || props.isFetchingOrder}
                    />
                  </NoPermissionTooltip>
                </Show>
                <NoPermissionTooltip shouldShowTooltip={shouldIssuePaymentButtonBeDisabledACL}>
                  <FormButton
                    control={control}
                    title={i18n.t('entity.checkout.actions.issuePayment')}
                    data-testid={suffixTestId('issuePayment', props)}
                    type="submit"
                    isDisabled={
                      (isAnyDepositUnpaid && props.payment.paymentDiscriminator !== 'DEPOSIT') ||
                      shouldIssuePaymentButtonBeDisabledACL ||
                      props.isFetchingOrder ||
                      (parseFloat(props.payment.amount?.amount ?? DEFAULT_PRICE) < 0 &&
                        props.payment.paymentDiscriminator === 'DEPOSIT')
                    }
                  />
                </NoPermissionTooltip>
              </Right>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}

// TODO - @martin.slaby / @jakub.bendzala "styled" made cypress build fail
// export const PredefinedNotesButtonWrapper = styled.div`
//   position: absolute;
//   right: 0;
//   top: -9px;
// `;

const getSchema = (
  isPartialPayment: boolean,
  orderDiscriminator: OrderDiscriminator,
  hasForeignCurrencyPayment: boolean
) =>
  object({
    amount: mixed().when([], {
      is: () => isPartialPayment,
      then: () => yupNumber.min(0).required(),
    }),
    percentage: mixed().when([], {
      is: () => isPartialPayment,
      then: () => yupNumber.min(0).max(100).required(),
    }),
    issuedDate: yupString.required(),
    dueDate: yupString.required(),
    dateOfTaxableSupply: yupString.required(),
    note: yupString,
    createCashRegisterDocument: boolean()
      .default(false)
      .when('paymentMethod', {
        is: (val: string) => val === 'CASH' || val === 'PAYMENT_CARD',
        then: (val) => val.required(),
      }),
    customerBankAccountId: yupString.when('paymentMethod', {
      is: (val: string) =>
        (val === 'BANK_TRANSFER' || val === 'OFFSET') &&
        (orderDiscriminator === 'PURCHASE' || orderDiscriminator === 'PURCHASE_BROKERAGE_SALE'),
      then: (val) => val.required(),
    }),
    exchangeRate: mixed().when([], {
      is: () => hasForeignCurrencyPayment,
      then: () =>
        yupString
          .transform((currentValue, originalValue) => {
            if (isNumber(originalValue)) {
              return originalValue.toString();
            }
            return currentValue;
          })
          .required()
          .test(
            'positive-exchange-rate',
            i18n.t('general.errors.number.positive'),
            (val) => isNotNil(val) && parseFloat(val) > 0
          ),
    }),
  });
